// src/client/renderer/vfx/items/dimension-rift.ts
//
// Plan 17, Task 4.2 — Dimension Rift pool (portal & swap visuals).
//
// Triggered by TWO events:
//   1. ItemCastEvent with itemId='dimension_rift' → phase='cast':
//        Portal swirl at caster position (600ms life).
//   2. ItemSwapPositionsEvent → phase='active':
//        TWO portal swirls — one at each old node, both 600ms life.
//
// Colour: 0xff66cc (magenta).
// Pool: one-shot, max-active=4 (cast + swap up to 2 victims at once = 4 slots).
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Swap portal visuals are
//   intentionally silent follow-up emitters.
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.

import * as THREE from 'three';
import type {
  GameEvent,
  ItemCastEvent,
  ItemSwapPositionsEvent,
} from '../../../../shared/game/types';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_ACTIVE = 4;

/** Portal-swirl lifetime in seconds (600ms). */
const LIFETIME = 0.6;

/** Portal / swap magenta colour. */
const RIFT_COLOUR = 0xff66cc;

// ─────────────────────────────────────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Sprite materials returned alongside the swirl group so the timeline can
 *  animate them without traversing children. */
interface SwirlMaterials {
  outerMaterial: THREE.SpriteMaterial;
  innerMaterial: THREE.SpriteMaterial;
}

/**
 * Build a single portal-swirl Group (outer ring + inner glow) positioned at
 * `anchor`.  The sprites start at scale=0 and are animated by the caller's
 * GSAP timeline.  Materials are returned so the timeline can drive opacity.
 */
function buildPortalSwirl(anchor: { x: number; y: number; z: number }): {
  swirlGroup: THREE.Group;
  materials: SwirlMaterials;
} {
  const swirlGroup = new THREE.Group();

  const outerMaterial = new THREE.SpriteMaterial({
    color: RIFT_COLOUR,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const outer = new THREE.Sprite(outerMaterial);
  outer.position.set(anchor.x, anchor.y, anchor.z);
  outer.scale.set(0.0, 0.0, 1);
  swirlGroup.add(outer);

  const innerMaterial = new THREE.SpriteMaterial({
    color: RIFT_COLOUR,
    transparent: true,
    opacity: 0.6,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const inner = new THREE.Sprite(innerMaterial);
  inner.position.set(anchor.x, anchor.y, anchor.z);
  inner.scale.set(0.0, 0.0, 1);
  swirlGroup.add(inner);

  return { swirlGroup, materials: { outerMaterial, innerMaterial } };
}

/**
 * Build a parent Group containing one or two portal-swirl children,
 * wire up a single GSAP timeline that drives all children's scales and
 * materials uniformly, and populate `state` with the timeline handle.
 *
 * - One position  → cast phase (single swirl).
 * - Two positions → active phase (two swirls, one per old-node anchor).
 */
function buildPortalEmitter(
  positions: ReadonlyArray<{ x: number; y: number; z: number }>,
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Build one swirl child per position.
  const allMaterials: SwirlMaterials[] = [];
  for (const pos of positions) {
    const { swirlGroup, materials } = buildPortalSwirl(pos);
    group.add(swirlGroup);
    allMaterials.push(materials);
  }

  const handle = registry.create('dimension_rift_portal');
  state.handle = handle;
  const tl = handle.timeline;

  // Phase 1 (0 → 300ms): expand each swirl's sprites from 0 → target scale.
  // Iterate swirlGroup children directly; each child is a Sprite.
  group.children.forEach((swirlGroup) => {
    const [outer, inner] = swirlGroup.children as THREE.Sprite[];
    tl.to(
      outer.scale,
      {
        x: 1.5,
        y: 1.5,
        z: 1,
        duration: LIFETIME * 0.5,
        ease: 'back.out(1.7)',
      },
      0,
    );
    tl.to(
      inner.scale,
      {
        x: 0.8,
        y: 0.8,
        z: 1,
        duration: LIFETIME * 0.5,
        ease: 'power2.out',
      },
      0,
    );
  });

  // Phase 2 (300ms → 600ms): fade out all sprites via a shared opacity proxy.
  // One proxy drives all materials uniformly.
  const opProxy = { outer: 1.0, inner: 0.6 };
  tl.to(
    opProxy,
    {
      outer: 0.0,
      inner: 0.0,
      duration: LIFETIME * 0.5,
      ease: 'power1.in',
      onUpdate: () => {
        for (const { outerMaterial, innerMaterial } of allMaterials) {
          outerMaterial.opacity = opProxy.outer;
          innerMaterial.opacity = opProxy.inner;
        }
      },
    },
    LIFETIME * 0.5,
  );

  // Mark finished at end of lifetime.
  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    LIFETIME,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createDimensionRiftPool
// ─────────────────────────────────────────────────────────────────────────────

export function createDimensionRiftPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;
  const emitterStates = new Map<THREE.Object3D, EmitterState>();
  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;
    if (emitterStates.size >= MAX_ACTIVE) return null;

    // For the active phase (item_swap_positions) both old-node positions are
    // available.  Spawn TWO portal swirls — one per old node.
    // For the cast phase (item_cast) only casterPosition is set; spawn one.
    const positions: Array<{ x: number; y: number; z: number }> = [];
    if (args.phase === 'active' && args.casterPosition && args.targetPosition) {
      positions.push(args.casterPosition, args.targetPosition);
    } else {
      const anchor = args.casterPosition ?? args.targetPosition;
      if (!anchor) return null;
      positions.push(anchor);
    }

    const state: EmitterState = { handle: null!, finished: false };
    const group = buildPortalEmitter(positions, deps, state);
    scene.add(group);
    emitterStates.set(group, state);
    return group;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    return state ? state.finished : true;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return;
    state.handle.dispose();
    scene.remove(emitter);
    disposeSkillshotGroup(emitter);
    emitterStates.delete(emitter);
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const [emitter, state] of emitterStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    emitterStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveDimensionRiftForEvent
// ─────────────────────────────────────────────────────────────────────────────

export function resolveDimensionRiftForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
  getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'dimension_rift'; args: ItemVfxSpawnArgs } | null {
  if (event.type === 'item_cast') {
    const cast = event as ItemCastEvent;
    if (cast.itemId !== 'dimension_rift') return null;
    return {
      poolId: 'dimension_rift',
      args: {
        phase: 'cast',
        casterId: cast.casterId,
        casterPosition: getPositionForPlayer?.(cast.casterId),
      },
    };
  }

  if (event.type === 'item_swap_positions') {
    const swap = event as ItemSwapPositionsEvent;
    return {
      poolId: 'dimension_rift',
      args: {
        phase: 'active',
        casterId: swap.casterId,
        // caster's old node position becomes casterPosition
        casterPosition: getPositionForNode?.(swap.casterOldNodeId),
        // target's old node position becomes targetPosition
        targetPosition: getPositionForNode?.(swap.targetOldNodeId),
      },
    };
  }

  return null;
}
